import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { AppInformationService, AuthService, GenesisUnitOfWork, UtilityService } from 'app/core/core.module';
import {
    Dealer,
    FeeType,
    Order,
    PackingSlip,
    Part,
    PartConfigAttachment,
    PartConfiguration,
    PartLink,
    PartPartType,
    PartType,
    PaymentLine,
    ShipAddress,
} from 'app/core/model/entity-model';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import { environment } from 'environments/environment';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PartDetailComponent } from '../part-detail/part-detail.component';
import { Location } from '@angular/common';

@Component({
    selector: 'app-part-configuration',
    templateUrl: './part-configuration.component.html',
    styleUrls: ['./part-configuration.component.scss'],
    standalone: false,
})
export class PartConfigurationComponent implements OnInit {
    uploadHeader: any;
    uploadUrl = `${environment.serviceName}/file/SavePartConfigAttachmentToBlobStorage`;
    attachmentUploadUrl = `${environment.serviceName}/file/SavePartAttachmentToBlobStorage`;

    data: any = {};
    attachments: PartConfigAttachment[] = [];
    partConfigurations: PartConfiguration[];
    partConfigurationLines: any[];
    currentBom: any;

    invoice: any;
    invoiceId: any;
    orderIds: number[];
    order: Order;
    packingSlipIds: number[];
    packingSlip: PackingSlip;
    dealer: Dealer;
    orderLineDetails: any[];

    invoiceLines: any;
    preSubmittedNote: string;
    dealers: Dealer[];
    paymentMethods: any;
    deliveryOptions: any;
    shipAddresses: ShipAddress[];
    readOnly = true;

    processing = false;
    showAsDialog = false;
    saving = false;

    canUpdatePricing = false;
    canAddDetails = false;
    achInfo: any;
    paymentLines: PaymentLine[];
    feeTypes: FeeType[];
    ready = false;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    @ViewChild('linesGrid') linesGrid: DxDataGridComponent;
    @ViewChild('attachmentsGrid') attachmentsGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    uow = inject(GenesisUnitOfWork);
    modalService = inject(BsModalService);
    location = inject(Location);

    constructor(public auth: AuthService, private appInfo: AppInformationService, private utility: UtilityService) {}

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges();
    }

    async ngOnInit() {
        this.uploadHeader = { Authorization: `Bearer ${this.auth.currentUser.jwtToken}` };
        this.invoice = {};
        await this.getData();
        await this.getAttachments();
    }
    hasChanges() {
        return this.uow.hasChanges();
    }

    async getData() {
        const partConfigurations = await this.uow.getPartConfigurations('lines');
        this.partConfigurations = partConfigurations as PartConfiguration[];
        this.setMissingPartsCount();
    }

    async setMissingPartsCount() {
        this.partConfigurations.forEach((x) => ((x as any).missingPartsCount = x.lines.filter((x) => !x.partId).length));
    }

    async onSelectionChanged(e) {
        if (e.selectedRowsData.length > 0) {
            const selectedPartConfiguration = e.selectedRowsData[0] as PartConfiguration;
            await this.updateLines(selectedPartConfiguration);
        } else {
            this.partConfigurationLines = [];
        }
    }

    async updateLines(selectedPartConfiguration: PartConfiguration) {
        const partConfigurationLines = selectedPartConfiguration.lines.map((line) => {
            return {
                id: line.id,
                partId: line.partId,
                partNumber: line.partNumber,
                description: line.description,
                quantity: line.quantity,
                partConfigurationId: line.partConfigurationId,
                currentPartNumber: null,
                currentDescription: null,
                currentQuantity: null,
            };
        });
        if (selectedPartConfiguration.partId && selectedPartConfiguration.lines?.length > 0) {
            const partLinks = await this.uow.getPartLinkByPartId(selectedPartConfiguration.partId);
            partLinks.forEach((link) => {
                const matchingLine = partConfigurationLines.find((line) => line.partNumber === link.subPart.partNumber) as any;
                if (matchingLine) {
                    matchingLine.currentPartNumber = link.subPart.partNumber;
                    matchingLine.currentDescription = link.subPart.description;
                    matchingLine.currentQuantity = link.quantity;
                } else
                    partConfigurationLines.push({
                        id: link.id,
                        partId: link.subPart.id,
                        partNumber: null,
                        description: null,
                        quantity: null,
                        partConfigurationId: null,

                        currentPartNumber: link.subPart.partNumber,
                        currentDescription: link.subPart.description,
                        currentQuantity: link.quantity,
                    });
            });
        }
        this.partConfigurationLines = partConfigurationLines;
    }

    rowPreparePartConfigurations(e) {
        if (e.rowType !== 'data') return;

        const partConfig = e.data;
        if (!partConfig.partId) {
            e.rowElement.className = e.rowElement.className + ' no-part';
        } else if (!!partConfig.partId && partConfig.lines?.length === 0) {
            e.rowElement.className = e.rowElement.className + ' no-part-config-lines';
        }
    }

    rowPreparePartConfigurationLines(e) {
        if (e.rowType !== 'data') return;
        const partConfig = e.data;
        if (!partConfig.partId) {
            e.rowElement.className = e.rowElement.className + ' no-part';
        } else if (!partConfig.partNumber && !!partConfig.currentPartNumber) {
            e.rowElement.className = e.rowElement.className + ' removing-config-lines';
        } else if (!!partConfig.partNumber && !partConfig.currentPartNumber) {
            e.rowElement.className = e.rowElement.className + ' new-config-lines';
        } else if (!!partConfig.partNumber && !!partConfig.currentPartNumber && partConfig.quantity === partConfig.currentQuantity) {
            e.rowElement.className = e.rowElement.className + ' matching-config-lines';
        }
    }

    getCurrentBOM() {}

    async getAttachments() {
        this.attachments = await this.uow.getPartConfigAttachments();
    }

    openAttachment(attachment) {
        window.open(attachment.filename, '_blank');
    }

    onToolbarPreparingAttachments(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarText',
            },
            {
                location: 'before',
                template: 'toolbarButtons',
            }
        );
    }

    uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
    }

    async configUploaded(e) {
        if (e?.request?.responseText) {
            const fileResponses = JSON.parse(e.request.responseText) as { filename: string; name: string }[];
            for (const fileResponse of fileResponses) {
                const attachment = this.uow.partConfigAttachments.createEntity({
                    name: fileResponse.name,
                    filename: fileResponse.filename,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                }) as PartConfigAttachment;
                this.attachments.push(attachment);
                await this.uow.commit([attachment]);
            }
            this.attachments = _(this.attachments).orderBy('name').valueOf();
        }
        this.appInfo.pleaseWaitHide();
    }

    async attachmentUploaded(e) {
        if (e?.request?.responseText) {
            const fileResponses = JSON.parse(e.request.responseText) as { filename: string; name: string }[];
            for (const fileResponse of fileResponses) {
                const partNumber = fileResponse.name.slice(0, fileResponse.name.lastIndexOf('.'));
                const part = await this.uow.getPartByPartNumber(partNumber);
                if (part) {
                    const attachment = this.uow.partAttachments.createEntity({
                        partId: part.id,
                        name: fileResponse.name,
                        filename: fileResponse.filename,
                        created: new Date(),
                        createdBy: this.auth.currentUser.userName,
                    });
                    await this.uow.commit([attachment]);
                    this.appInfo.notifyToastStack(`Attachment ${fileResponse.name} uploaded to part ${partNumber}`, 'success');
                    console.log(`Attachment ${fileResponse.name} uploaded to part ${partNumber}`);
                } else {
                    this.appInfo.notifyToastStack(`Part not found for attachment: ${fileResponse.name}`, 'error');
                    continue;
                }
            }
            // this.appInfo.notifyToast('Part Drawing Uploaded', 'success');
        }
        this.appInfo.pleaseWaitHide();
    }

    uploadError(e) {
        this.appInfo.pleaseWaitHide();
        alert('An error occurred while attempting to upload the file(s)', 'Upload Error');
    }

    async removeAttachment(e: any, attachment: PartConfigAttachment) {
        e.preventDefault();
        attachment.deleted = new Date();
        this.attachments.splice(this.attachments.indexOf(attachment), 1);
        await this.uow.commit([attachment]);
    }

    async processAttachments() {
        if (this.attachments.length === 0) return;
        this.appInfo.pleaseWaitShow();
        try {
            await this.utility.processPartConfigurationAttachments();
            await this.getAttachments();
            await this.getData();
            this.appInfo.notifyToast('Part Configuration Attachments Processed', 'success');
        } catch (error) {
            this.appInfo.notifyToast('Error processing attachments', 'error');
            this.appInfo.pleaseWaitHide();
            return;
        }

        this.appInfo.pleaseWaitHide();
    }

    async deleteConfigurationAttachments() {
        const selectedRows = this.attachmentsGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;
        const result = await confirm(
            `Are you sure you want to delete ${selectedRows.length} uploaded configuration${selectedRows.length === 1 ? '' : 's'} without processing?`,
            `${this.appInfo.settings.company}`
        );
        if (!result) return;
        this.appInfo.pleaseWaitShow();
        selectedRows.forEach((selectedRow) => {
            selectedRow.deleted = new Date();
        });
        await this.uow.commit();
        await this.getAttachments();
        this.appInfo.pleaseWaitHide();
    }

    async refresh() {
        this.appInfo.pleaseWaitShow();
        await this.utility.updatePartConfigurations(this.partConfigurations.map((x) => x.id));
        await this.getData();
        if (this.dataGrid.instance.getSelectedRowsData().length > 0) {
            await this.updateLines(this.dataGrid.instance.getSelectedRowsData()[0]);
        }
        this.appInfo.pleaseWaitHide();
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarText',
            },
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            }
        );
    }

    async deletePartConfiguration() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;
        const result = await confirm(
            `Are you sure you want to delete ${selectedRows.length} Part Configuration${selectedRows.length === 1 ? '' : 's'}?`,
            `${this.appInfo.settings.company}`
        );
        if (!result) return;
        this.appInfo.pleaseWaitShow();
        selectedRows.forEach((selectedRow) => {
            selectedRow.deleted = new Date();
            selectedRow.lines.forEach((line) => {
                line.deleted = new Date();
            });
        });
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
        this.appInfo.notifyToast(`${selectedRows.length} Part Configuration${selectedRows.length === 1 ? '' : 's'} Deleted`, 'success');
    }

    async createSubPart(line) {
        const modalRef = this.modalService.show(PartDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                partId: 'new',
                createSubPartMode: true,
                partConfigurationLine: line,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            modalRef.hide();
            if (result.partId) {
                this.appInfo.pleaseWaitShow();
                this.partConfigurations.forEach((x) =>
                    x.lines.forEach((l) => {
                        if (l.partNumber === line.partNumber) {
                            l.partId = result.partId;
                        }
                    })
                );
                await this.uow.commit();
                await this.updateLines(this.partConfigurations.filter((x) => x.id === line.partConfigurationId)[0]);
                this.setMissingPartsCount();
                this.appInfo.pleaseWaitHide();
            }
        });
    }

    async createAll() {
        const configs = this.partConfigurations.filter((x) => !x.partId && (x as any).missingPartsCount === 0);
        if (configs.length === 0) {
            await alert(`There are no parts to create.`, this.appInfo.settings.company);
            return;
        }
        const success = await confirm(`Are you sure you want to create ${configs.length} part${configs.length === 1 ? '' : 's'}?`, this.appInfo.settings.company);
        if (!success) return;

        this.appInfo.pleaseWaitShow();
        for (const config of configs) {
            this.createPart(config);
        }

        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async createSinglePart(config) {
        this.createPart(config);
        await this.uow.commit();
        await this.getData();
    }

    createPart(config) {
        const part = this.uow.parts.createEntity() as Part;
        part.reportGuid = crypto.randomUUID();
        part.created = new Date();
        part.inStock = 0;
        part.partNumber = config.partNumber;
        part.mfgPartNumber = config.partNumber;
        part.description = config.description;
        part.mfgDescription = config.description;
        part.category = 'NEW';
        part.fontColor = 'rgba(166, 108, 38, 1)';
        const partPartType = this.uow.partPartTypes.createEntity() as PartPartType;
        partPartType.part = part;
        partPartType.partTypeId = PartType.Types.Trailer;
        config.lines.forEach((x) => {
            var partLink = this.uow.partLinks.createEntity() as PartLink;
            partLink.part = part;
            partLink.subPartId = x.partId;
            partLink.quantity = x.quantity;
            partLink.created = new Date();
            partLink.createdBy = this.auth.currentUser.userName;
        });
        config.part = part;
        config.processed = new Date();
    }

    async process(configs: any[]) {
        const success = await confirm(
            `Are you sure you want to process ${configs.length} part configuration${configs.length === 1 ? '' : 's'}? This will replace existing part trees.`,
            this.appInfo.settings.company
        );
        if (!success) return;
        this.appInfo.pleaseWaitShow();
        const processedCount = (await this.utility.processPartConfigurations(
            configs.map((x) => x.id),
            this.auth.currentUser.userName
        )) as number;
        await this.getData();
        this.appInfo.pleaseWaitHide();
        let msg = `${processedCount} part configuration${processedCount === 1 ? '' : 's'} successfully processed.`;
        let failedCount = configs.length - processedCount;
        if (failedCount > 0) {
            msg += `\n${failedCount} part configuration${failedCount === 1 ? '' : 's'} could not be processed due to missing parts.`;
        }
        await alert(msg, this.appInfo.settings.company);
    }
}
